import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  AnalysisRcaFilterModel,
  IAnalysisRcaFilterModel,
} from '../../../../../filters/analysis-rca-filter.modal'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { NgbActiveModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap'
import { merge, Observable, of, Subject } from 'rxjs'
import { HelperService } from '../../../../../services/helper.service'
import { RegexEnum } from '../../../../../enums/regex.enum'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators'
import { notPattern } from '../../../../../validators/not-pattern.validator'
import { atLeast } from '../../../../../validators/at-least.validator'
import IAnalysisService from '../i-analysis.service'

export type FormType = {
  sites: FormControl<string[] | null>
  tagName: FormControl<string | null>
  rcaNo: FormControl<string | null>
  title: FormControl<string | null>
}

@Component({
    selector: 'app-analysis-filters-modal',
    templateUrl: './analysis-filters-modal.component.html',
    standalone: false
})
export class AnalysisFiltersModalComponent implements OnInit {
  @Input() type: 'F' | 'D'
  @Input() filter: AnalysisRcaFilterModel
  @Input() service: IAnalysisService
  @Input() availableSites: string[] = []

  public formFilter: FormGroup<FormType>
  public sites: FormControl<string[] | null>
  public tagName: FormControl<string | null>
  public rcaNo: FormControl<string | null>
  public title: FormControl<string | null>

  private _appliedFilter: any = {}

  /** Type Head Definition */
  @ViewChild('instanceRN', { static: false }) instanceRN: NgbTypeahead
  @ViewChild('instanceTN', { static: false }) instanceTN: NgbTypeahead

  public activeSearch: { rn: boolean; tn: boolean } = {
    rn: false,
    tn: false,
  }

  public failSearch: { rn: boolean; tn: boolean } = {
    rn: false,
    tn: false,
  }

  public clicks$: any = {
    rn: new Subject<string>(),
    tn: new Subject<string>(),
  }

  public focuses$: any = {
    rn: new Subject<string>(),
    tn: new Subject<string>(),
  }

  constructor(private activeModal: NgbActiveModal) {}

  /** Type Head RCA No */
  public isRNFilled() {
    return this.rcaNo.value && this.rcaNo.value.length > 0
  }

  public resetRN(input: HTMLInputElement): void {
    this.rcaNo.setValue(null)
    this.rcaNo.markAsDirty()
    input.click()
  }

  public getRNs(keyword: string): Observable<any> {
    this.activeSearch.rn = true
    const inputVal = this.formFilter.get('rcaNo').value
    const key = keyword.length === 0 && inputVal ? inputVal : keyword
    return this.service.getRCAItems(key, 10).pipe(
      map((result: any) => {
        this.activeSearch.rn = false
        this.failSearch.rn = false
        return result
      }),
      catchError(() => {
        this.activeSearch.rn = false
        this.failSearch.rn = true
        return of([])
      }),
    )
  }

  public searchRN = (text$: Observable<string>) => {
    const debouncedText$: Observable<string> = text$.pipe(debounceTime(500), distinctUntilChanged())
    const clicksWithClosedPopup$ = this.clicks$.rn.pipe(
      filter(() => !this.instanceRN.isPopupOpen()),
    )
    return merge(debouncedText$, this.focuses$.rn, clicksWithClosedPopup$).pipe(
      switchMap(term => this.getRNs(`${term}`)),
    )
  }
  /** Type Head RCA No */

  /** Type Head TagName */
  public isTNFilled() {
    return this.tagName.value && this.tagName.value.length > 0
  }

  public resetTN(input: HTMLInputElement): void {
    this.tagName.setValue(null)
    this.tagName.markAsDirty()
    input.click()
  }

  public getAllTags(keyword: string): Observable<any> {
    this.activeSearch.tn = true
    const inputVal = this.formFilter.get('tagName').value
    const key = keyword.length === 0 && inputVal ? inputVal : keyword
    return this.service.getTagItems(key, 10).pipe(
      map((result: any) => {
        this.activeSearch.tn = false
        this.failSearch.tn = false
        return result
      }),
      catchError(() => {
        this.activeSearch.tn = false
        this.failSearch.tn = true
        return of([])
      }),
    )
  }

  public searchTN = (text$: Observable<string>) => {
    const debouncedText$: Observable<string> = text$.pipe(debounceTime(500), distinctUntilChanged())
    const clicksWithClosedPopup$ = this.clicks$.tn.pipe(
      filter(() => !this.instanceTN.isPopupOpen()),
    )
    return merge(debouncedText$, this.focuses$.tn, clicksWithClosedPopup$).pipe(
      switchMap(term => this.getAllTags(`${term}`)),
    )
  }
  /** Type Head TagName */

  public getRCALabel(): string {
    if (this.type === 'F') {
      return 'RCA Fault No'
    }
    if (this.type === 'D') {
      return 'RCA Demand No'
    }
    return 'RCA No'
  }

  public applyFilters(): void {
    if (this.formFilter.invalid) {
      return
    }
    this.activeModal.close({
      filter: this.getFilter(),
      changed: this.filterChanged(),
    })
  }

  private getFilter(): AnalysisRcaFilterModel {
    const clonedForm: IAnalysisRcaFilterModel = HelperService.deepCopy(this.formFilter.value)
    return AnalysisRcaFilterModel.fromJson(clonedForm)
  }

  private filterChanged(): boolean {
    return (
      JSON.stringify(HelperService.getObjectWithoutNullValues(this._appliedFilter)) !==
      JSON.stringify(HelperService.getObjectWithoutNullValues(this.getFilter()))
    )
  }

  private initFormControls(): void {
    this.sites = new FormControl<string[] | null>(null)
    this.tagName = new FormControl<string|null>(null, [
      Validators.pattern(RegexEnum.REGEX_MO),
      notPattern(RegexEnum.REGEX_NOT_WILDCARD),
    ])
    this.rcaNo = new FormControl<string|null>(null, [
      Validators.pattern(RegexEnum.REGEX_MO),
      notPattern(RegexEnum.REGEX_NOT_WILDCARD),
    ])
    this.title = new FormControl<string|null>(null, [notPattern(RegexEnum.REGEX_NOT_WILDCARD)])
  }

  private initFormGroup(): void {
    this.formFilter = new FormGroup<FormType>(
      {
        sites: this.sites,
        tagName: this.tagName,
        rcaNo: this.rcaNo,
        title: this.title,
      },
      {
        validators: [atLeast(['sites', 'tagName', 'rcaNo', 'title'], 1)],
      },
    )
  }

  private patchForm(filter: IAnalysisRcaFilterModel): void {
    const formData: any = {
      sites: filter.sites && filter.sites.length > 0 ? filter.sites : null,
      tagName: filter.tagName ? filter.tagName : null,
      rcaNo: filter.rcaNo ? filter.rcaNo : null,
      title: filter.title ? filter.title : null,
    }
    this.formFilter.patchValue(formData)
  }

  public canDisplayMultiSiteFilter(): boolean {
    return this.availableSites
      && this.availableSites.length > 1
  }

  ngOnInit(): void {
    this.initFormControls()
    this.initFormGroup()
    if (this.filter) {
      this.patchForm(this.filter)
      this._appliedFilter = HelperService.deepCopy(this.filter)
    }
  }
}
